import Cookies from "js-cookie";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import backIcon from "../assets/backIcon.png";
import downloadIcon from "../assets/export-icon.png";

import PdfViewer from "../components/PdfViewer";
import Modal from "../components/Modal";
import MLetterViewer from "../components/MLetterViewer";
import TranslationModal from "../components/TranslationModal";
import { gsap } from "gsap";

import ProfileHeader from "../components/Analysis/ProfileHeader";
import BigFiveChart from "../components/Analysis/BigFiveChart";
import DISCModel from "../components/Analysis/DISCModel";
import RIASECModel from "../components/Analysis/RIASECModel";
import MBTISection from "../components/Analysis/MBTISection";
import Recommendations from "../components/Analysis/Recommendations";
import AdvancedAnalysis from "../components/Analysis/AdvancedAnalysis";
import FilesSection from "../components/Analysis/FilesSection";
import SelfAssessment from "../components/Analysis/SelfAssessment";

import ViewModeToggle from "../components/ViewModeToggle";

import Error from "../components/Error";

import useGeneratePDF from "../hooks/useGeneratePDF";
import useChartData from "../hooks/useChartData";
import useTranslateAnalysis from "../hooks/useTranslateAnalysis";

const translations = {
  en: {
    back: "Back",
    title: "Personality Analysis Report",
    downloadPDF: "Download as PDF",
    generatingPDF: "Generating PDF...",
    loading: "Loading report...",
  },
  nl: {
    back: "Terug",
    title: "Persoonlijkheidsanalyse Rapport",
    downloadPDF: "Download als PDF",
    generatingPDF: "PDF genereren...",
    loading: "Rapport laden...",
  },
};

function ReportDetails({
  isGeneratingPDF,
  setIsGeneratingPDF,
  isRecruiterMode,
  setIsRecruiterMode,
}) {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [linkedInData, setLinkedInData] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMLetterModalOpen, setIsMLetterModalOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const chartRef = useRef(null);
  const [questionnaire, setQuestionnaire] = useState(null);

  const reportId = window.location.pathname.split("/").pop();

  const colors = [
    "#FFD700",
    "#FFA500",
    "#FF8C00",
    "#EA650D",
    "#E55100",
    "#D84315",
  ];

  const [isAccountAdmin, setIsAccountAdmin] = useState(false);

  const [lang, setLang] = useState(
    Cookies.get("isDutch") === "true" ? "nl" : "en"
  );
  const t = translations[lang];

  const { translatedResults, isTranslating } = useTranslateAnalysis(
    report?.Analysis || null
  );

  useEffect(() => {
    const accountType = Cookies.get("accountType");
    if (!accountType) return;

    if (accountType === "4") {
      setIsRecruiterMode(false);
    } else {
      setIsRecruiterMode(true);
    }

    if (accountType === "1" || accountType === "2") {
      setIsAccountAdmin(true);
    } else {
      setIsAccountAdmin(false);
    }
  }, [setIsRecruiterMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      const cookieLang = Cookies.get("isDutch") === "true" ? "nl" : "en";
      if (cookieLang !== lang) setLang(cookieLang);
    }, 1000);
    return () => clearInterval(interval);
  }, [lang]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/report/new/${reportId}`,
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("token")}`,
              "Content-Type": "application/json",
              accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch reports");
        }

        const data = await response.json();
        if (data.successful) {
          setReport(data.data);
          setAnalysisResults(data.data.Analysis);
          setLinkedInData(data.data.linkedInData);

          // Update Big Five chart data
          const bigFiveScores = Object.entries(data.data.Analysis.bigFive)
            .filter(([trait]) => trait !== "workBehavior")
            .map(([_, data]) => data.score);

          setBigFiveData((prev) => ({
            ...prev,
            datasets: [
              {
                ...prev.datasets[0],
                data: bigFiveScores,
              },
            ],
          }));

          const questionnaireId = data.data?.questionnaire_id;

          if (questionnaireId) {
            const questionnaireRes = await fetch(
              `${process.env.REACT_APP_API_URL}/api/report/questionnaire/${questionnaireId}`,
              {
                headers: {
                  Authorization: `Bearer ${Cookies.get("token")}`,
                  "Content-Type": "application/json",
                  accept: "application/json",
                },
              }
            );
            if (questionnaireRes.ok) {
              const questionnaireData = await questionnaireRes.json();
              setQuestionnaire(questionnaireData.data || null);

              console.log("questionaire", questionnaireData);
            } else {
              setQuestionnaire(null); 
            }
          } else {
            setQuestionnaire(null); 
          }
        } else {
          setError(data.error || "Failed to fetch reports");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  const {
    bigFiveData,
    setBigFiveData,
    discData,
    setDiscData,
    riasecData,
    setRiasecData,
    mbtiData,
    setMbtiData,
  } = useChartData();
  const {
    generatePDF,
    generatingPDF,
    error: pdfError,
  } = useGeneratePDF(setIsGeneratingPDF, linkedInData);

  const handleGeneratePDF = async () => {
    await generatePDF(
      "analysis-report",
      `${linkedInData.firstName}_${linkedInData.lastName}`
    );
  };

  const openPdfModal = (base64) => {
    setPdfBase64(base64);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!loading && report) {
      const sections = document.querySelectorAll(".animate-text");
      if (sections.length === 0) return;

      gsap.fromTo(
        sections,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.2,
          ease: "back",
        }
      );
    }
  }, [loading, report]);

  useEffect(() => {
    const mainElement = document.querySelector(".main");
    if (mainElement) {
      if (isGeneratingPDF) {
        mainElement.classList.add("mt-neg-active");
      } else {
        mainElement.classList.remove("mt-neg-active");
      }
    }
  }, [isGeneratingPDF]);

  return (
    <div className="m-0 text-left pb-10 main">
      {/* {error && <Error error={error} />} */}

      <TranslationModal isGeneratingPDF={isGeneratingPDF} isOpen={isTranslating} isDutch={lang === "nl"} />

      {!isGeneratingPDF && (
        <div className="flex justify-between items-center">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/reports")}
          >
            <img src={backIcon} alt="back" className="w-5 h-5" />
            <p>{t.back}</p>
          </div>
          {isAccountAdmin && (
            <ViewModeToggle
              isRecruiterMode={isRecruiterMode}
              setIsRecruiterMode={setIsRecruiterMode}
              isDutch={lang === "nl"}
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-4 mt-8 gap-4">
        <h1 className="text-5xl font-bold text-primary">{t.title}</h1>
        {!isGeneratingPDF && (
          <button
            id="download-button"
            className="border px-4 py-2 rounded-md flex items-center gap-2 bg-buttonColor"
            onClick={handleGeneratePDF}
          >
            <img src={downloadIcon} alt="download" className="w-4 h-4" />
            <p className="text-white truncate">
              {generatingPDF ? t.generatingPDF : t.downloadPDF}
            </p>
          </button>
        )}
      </div>

      <main id="analysis-report" className="flex flex-col gap-8">
        {loading ? (
          <p>{t.loading}</p>
        ) : report ? (
          <>
            <ProfileHeader
              linkedInData={linkedInData}
              analysisResults={translatedResults || report.Analysis}
              isRecruiterMode={isRecruiterMode}
              isGeneratingPDF={isGeneratingPDF}
              isDutch={lang === "nl"}
            />
            <AdvancedAnalysis
              analysisResults={translatedResults || report.Analysis}
              isRecruiterMode={isRecruiterMode}
              isGeneratingPDF={isGeneratingPDF}
              isDutch={lang === "nl"}
            />
            <section className="flex flex-col gap-8">
              <BigFiveChart
                bigFiveData={bigFiveData}
                analysisResults={translatedResults || report.Analysis}
                isRecruiterMode={isRecruiterMode}
                isGeneratingPDF={isGeneratingPDF}
                isDutch={lang === "nl"}
              />

              <section className="grid grid-cols-2 gap-4">
                <DISCModel
                  discData={discData}
                  analysisResults={translatedResults || report.Analysis}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={lang === "nl"}
                />
                <RIASECModel
                  riasecData={riasecData}
                  analysisResults={translatedResults || report.Analysis}
                  colors={colors}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={lang === "nl"}
                />
              </section>
              <MBTISection
                mbtiData={mbtiData}
                analysisResults={translatedResults || report.Analysis}
                colors={colors}
                isRecruiterMode={isRecruiterMode}
                isGeneratingPDF={isGeneratingPDF}
                isDutch={lang === "nl"}
              />

              <Recommendations
                analysisResults={translatedResults || report.Analysis}
                isRecruiterMode={isRecruiterMode}
                isGeneratingPDF={isGeneratingPDF}
                isDutch={lang === "nl"}
              />

              {!isGeneratingPDF && (
                <FilesSection
                  report={report}
                  openPdfModal={openPdfModal}
                  setError={setError}
                />
              )}

              {questionnaire && (
                <SelfAssessment
                  analysisResults={translatedResults || report.Analysis}
                  isRecruiterMode={isRecruiterMode}
                  isGeneratingPDF={isGeneratingPDF}
                  isDutch={lang === "nl"}
                  questionnaire={questionnaire}
                />
              )}
            </section>
          </>
        ) : (
          <p>{t.loading}</p>
        )}
      </main>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <PdfViewer base64={pdfBase64} onClose={() => setIsModalOpen(false)} />
      </Modal>
      <Modal
        isOpen={isMLetterModalOpen}
        onClose={() => setIsMLetterModalOpen(false)}
      >
        <MLetterViewer
          base64={pdfBase64}
          onClose={() => setIsMLetterModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

export default ReportDetails;
